/**
 * Friendly chart-axis labels for bucketed PERIOD columns.
 *
 * The Ask/assistant query engine truncates a date to a period bucket
 * (`day|week|month|quarter|year`, see `hub.ask.query._date_bucket`), so a
 * monthly time-series arrives with x-values like `2022-12-01T00:00:00` — every
 * bucket lands on the FIRST instant (midnight) of its period. Plotted verbatim
 * that reads as an ugly ISO timestamp on the axis (the owner's screenshot). This
 * formats such a column into locale-aware labels ("Dic 2022", "2022", "Q1 2022").
 *
 * Why here (frontend), not in the engine: it must cover BOTH chart sources —
 * a bucketed `query_dataset`/`run_metric` chart AND a `compute`-derived chart
 * where the model's Python carried the bucketed ISO string into its output
 * rows. The browser sees the final label values regardless of origin; the
 * backend only knows the grain on the first path.
 *
 * Safety rules so it never mangles a categorical column nor loses data — it is
 * STRICTLY better than the raw strings, or it returns them untouched:
 *   1. ALL labels must parse as a valid ISO date boundary — one non-date (or
 *      impossible, e.g. `2023-04-31`) value and the whole array is returned as-is.
 *   2. ALL values must sit at MIDNIGHT — a clean period bucket has no time-of-day.
 *      If any carries a real time (an intraday timestamp a `compute` chart may
 *      produce), formatting would drop it and could collapse two distinct points
 *      onto one tick, so we pass the column through untouched instead.
 *   3. UTC-pinned (`Intl.DateTimeFormat` with `timeZone:"UTC"`) so a midnight
 *      bucket never shifts a day in a negative-offset zone.
 *   4. A final non-lossy guard: if formatting ever produces FEWER distinct labels
 *      than there were distinct inputs (any merge), bail to raw — Chart.js's
 *      category axis renders duplicate labels ambiguously.
 *
 * Known limitation (documented, not a bug): grain is inferred from the SHAPE of
 * the value SET, which is unambiguous for a DENSE bucket but not for sparse data
 * that coincidentally lands only on coarser boundaries (e.g. a monthly series
 * present only in Jan/Apr/Jul/Oct reads as quarters; a weekly series whose every
 * Monday is a month's 1st reads as months). The labels stay date-correct and
 * unique — only the period-TYPE word may be coarser than the true bucket. The
 * robust cure is to thread the engine-known grain down for the query path; the
 * compute path can't supply it, so inference is the floor regardless.
 */

/** ISO date (`YYYY-MM-DD`) optionally with a time part — `T`/space separated,
 * optional seconds / fractional / zone. Capture H/M/S so we can tell a clean
 * midnight bucket from a real intraday timestamp. */
const ISO_DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

interface ParsedDate {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
  hasTime: boolean; // a non-midnight time-of-day was present
}

type Grain = "year" | "quarter" | "month" | "day";

/** Parse an ISO date-boundary string, or `null` if it isn't a REAL date. Rejects
 * impossible calendar dates (Apr 31, Feb 29 in a non-leap year) by round-tripping
 * through `Date` — otherwise `Date.UTC` rolls the overflow into the next month and
 * silently shifts the tick to the wrong period. */
function parseIsoBoundary(v: string): ParsedDate | null {
  const m = ISO_DATE_RE.exec(v);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d)
    return null; // calendar overflow → not a real date
  const hasTime = Number(m[4] ?? 0) !== 0 || Number(m[5] ?? 0) !== 0 || Number(m[6] ?? 0) !== 0;
  return { y, m: mo, d, hasTime };
}

/** Infer the bucket grain from the SET of (midnight) dates: a dense truncated
 * bucket has every value on its period boundary, so "all Jan 1" ⇒ year, "all
 * quarter-start" ⇒ quarter, "all 1st-of-month" ⇒ month, else ⇒ day. This is a
 * heuristic over value shape (see the module's "Known limitation"): correct for
 * dense buckets, can over-coarsen sparse data that only hits wider boundaries. */
function inferGrain(dates: ParsedDate[]): Grain {
  if (dates.every((p) => p.d === 1 && p.m === 1)) return "year";
  if (dates.every((p) => p.d === 1 && (p.m === 1 || p.m === 4 || p.m === 7 || p.m === 10)))
    return "quarter";
  if (dates.every((p) => p.d === 1)) return "month";
  return "day";
}

/** Upper-case the first character of a locale-formatted label ("gen 2022" →
 * "Gen 2022"). `Intl` emits lower-case month names in many locales (it/fr/es);
 * a leading capital reads better as a standalone axis tick. No-op when the
 * label starts with a digit (day grain → "1 gen 2022"). */
function capitalizeFirst(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatOne(p: ParsedDate, grain: Grain, locale: string): string {
  if (grain === "year") return String(p.y);
  if (grain === "quarter") return `Q${Math.floor((p.m - 1) / 3) + 1} ${p.y}`;
  // month / day → Intl, UTC-pinned so the bucket date never drifts a day.
  const date = new Date(Date.UTC(p.y, p.m - 1, p.d));
  const opts: Intl.DateTimeFormatOptions =
    grain === "month"
      ? { month: "short", year: "numeric", timeZone: "UTC" }
      : { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" };
  return capitalizeFirst(new Intl.DateTimeFormat(locale, opts).format(date));
}

/**
 * Map an array of raw x-axis label strings to friendly period labels when (and
 * ONLY when) every label is a valid, midnight ISO date boundary AND formatting
 * stays non-lossy. Otherwise the input is returned unchanged — the caller can
 * pass any x column blindly.
 *
 * @param raw    the x-values as strings (already `String(...)`-coerced).
 * @param locale a BCP-47 / i18n tag ("it", "en", …) for `Intl`.
 */
export function formatPeriodLabels(raw: string[], locale: string): string[] {
  if (raw.length === 0) return raw;
  const parsed = raw.map(parseIsoBoundary);
  if (parsed.some((p) => p === null)) return raw; // not a pure date column → leave it
  const dates = parsed as ParsedDate[];
  if (dates.some((p) => p.hasTime)) return raw; // intraday timestamps → not clean buckets
  const grain = inferGrain(dates);
  const out = dates.map((p) => formatOne(p, grain, locale));
  // Non-lossy guard: never merge two distinct x-values into one axis tick.
  if (new Set(out).size < new Set(raw).size) return raw;
  return out;
}
